import * as React from 'react';
import { useMemo, useState } from 'react';

import { Archive, Edit, Store, Truck } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { RequirePermission, usePermissions } from '@klareospace/pharos-lib-front-utils';
import { joinStrings } from '@klareospace/pharos-lib-utils';
import type { SortDirection, VehicleWithResolvedRefs } from '@klareospace/pharos-lib-types';
import type { DataTableColumn, RowAction, SimplePagination } from '@klareospace/pharos-lib-web-utils';
import {
  createActionsColumn,
  DataTable,
  RowActionsMenu,
  useDataTablePagination,
  useDataTableSorting,
  VehicleProfileIcon,
  VehicleStatusBadge,
} from '@klareospace/pharos-lib-web-utils';

import { PersonIcon } from '&/shared/components';

import type { VehicleScope, VehicleSortableColumn } from '../types/vehicle.types';
import { ArchiveVehicleDialog } from './archive-vehicle-dialog';
import { VehicleDrawer } from './vehicle-drawer';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface VehiclesSorting {
  onSort: (sortBy: VehicleSortableColumn, sortDirection: SortDirection | null) => void;
  sortBy: VehicleSortableColumn | undefined;
  sortDirection: SortDirection | undefined;
}

export interface VehiclesTableProps {
  carrierId?: string;
  isLoading: boolean;
  pagination: SimplePagination;
  scope: VehicleScope;
  siteId?: string;
  sorting: VehiclesSorting;
  spaceId?: string;
  vehicles: VehicleWithResolvedRefs[];
}

// ─── Component ────────────────────────────────────────────────────────────────

export function VehiclesTable({
  carrierId,
  isLoading,
  pagination,
  scope,
  siteId,
  sorting,
  spaceId,
  vehicles,
}: VehiclesTableProps): React.JSX.Element {
  const { t: tCommon } = useTranslation('common');
  const { t } = useTranslation('settings');
  const { hasPermission } = usePermissions();

  const [editingVehicle, setEditingVehicle] = useState<VehicleWithResolvedRefs | null>(null);
  const [archiveTarget, setArchiveTarget] = useState<string>('');

  const getRowActions = useMemo(
    () =>
      (vehicle: VehicleWithResolvedRefs): RowAction[] => {
        const actions: RowAction[] = [
          {
            icon: <Edit className="h-4 w-4" />,
            label: tCommon('actions.edit'),
            onClick: (): void => setEditingVehicle(vehicle),
          },
        ];

        if (hasPermission('vehicles:manage') && vehicle.deletedAt === null) {
          actions.push({
            className: 'text-red-600',
            hasSeparator: true,
            icon: <Archive className="h-4 w-4" />,
            label: tCommon('actions.archive'),
            onClick: (): void => setArchiveTarget(vehicle.id),
          });
        }

        return actions;
      },
    [hasPermission, t],
  );

  const columns = useMemo<DataTableColumn<VehicleWithResolvedRefs>[]>(
    () => [
      {
        cell: (vehicle): React.ReactNode => (
          <button
            className="cursor-pointer border-none bg-transparent p-0 font-mono text-sm font-medium hover:underline"
            onClick={(): void => setEditingVehicle(vehicle)}
            type="button"
          >
            {vehicle.licensePlate}
          </button>
        ),
        cellClassName: 'truncate',
        className: 'w-[20%] min-w-[140px]',
        header: t('common.licensePlate'),
        key: 'licensePlate',
        sortable: true,
      },
      {
        cell: (vehicle): React.ReactNode => {
          const siteNames = joinStrings(vehicle.resolvedSites?.map((s) => s.name));
          return (
            <div className="flex flex-col gap-0.5 text-sm">
              <span className="flex items-center gap-1 text-muted-foreground">
                <Truck className="h-3 w-3 shrink-0" />
                <span className="truncate max-w-[200px]">{vehicle.resolvedCarrier?.name ?? '—'}</span>
              </span>
              <span className="flex items-center gap-1 text-muted-foreground">
                <Store className="h-3 w-3 shrink-0" />
                <span className="truncate max-w-[200px]">{siteNames || t('common.allSites')}</span>
              </span>
            </div>
          );
        },
        cellClassName: 'truncate',
        className: 'w-[15%] min-w-[120px]',
        header: tCommon('fields.scope'),
        key: 'scope',
      },
      {
        cell: (vehicle): React.ReactNode => (
          <div className="flex items-center gap-2">
            <VehicleProfileIcon
              className="h-4 w-4 text-muted-foreground"
              profile={vehicle.resolvedVehicleTemplate?.profile ?? 'lightweight-vehicle'}
            />
            <span className="text-sm font-medium">
              {vehicle.resolvedVehicleTemplate?.name ?? vehicle.vehicleTemplateId}
            </span>
          </div>
        ),
        cellClassName: 'truncate',
        className: 'w-[15%] min-w-[140px]',
        header: t('common.vehicleTemplate'),
        key: 'vehicleTemplate',
      },
      {
        cell: (vehicle): React.ReactNode => (
          <VehicleStatusBadge label={t(`vehicles.statuses.${vehicle.status}`)} status={vehicle.status} />
        ),
        className: 'w-[15%] min-w-[120px]',
        header: tCommon('fields.status'),
        key: 'status',
        sortable: true,
      },
      {
        cell: (vehicle): React.ReactNode => {
          const driver = vehicle.resolvedDefaultDriver;
          if (!driver) {
            return <span className="text-xs text-muted-foreground">{tCommon('statuses.unassigned')}</span>;
          }
          return (
            <div className="flex items-center gap-2">
              <PersonIcon name={driver.name} />
              <span className="text-sm">{driver.name}</span>
            </div>
          );
        },
        className: 'w-[15%] min-w-[120px]',
        header: t('common.defaultDriver'),
        key: 'defaultDriver',
      },
      createActionsColumn<VehicleWithResolvedRefs>((vehicle) => (
        <RequirePermission permission="vehicles:manage">
          <RowActionsMenu actions={getRowActions(vehicle)} ariaLabel={tCommon('table.actionsHeader')} />
        </RequirePermission>
      )),
    ],
    [getRowActions, t],
  );

  const paginationLabels = useMemo(
    () => ({
      nextPage: tCommon('pagination.nextPage'),
      pageLabel: (current: number, total: number): string => tCommon('pagination.pageLabel', { current, total }),
      previousPage: tCommon('pagination.previousPage'),
      showingText: (start: number, end: number, total: number): string =>
        tCommon('pagination.showing', { end, start, total }),
    }),
    [tCommon],
  );

  const tablePagination = useDataTablePagination({ labels: paginationLabels, pagination });

  const tableSorting = useDataTableSorting<VehicleSortableColumn>({
    onSort: sorting.onSort,
    sortBy: sorting.sortBy,
    sortDirection: sorting.sortDirection,
  });

  return (
    <>
      <DataTable<VehicleWithResolvedRefs>
        ariaLabel={t('vehicles.table.ariaLabel')}
        columns={columns}
        emptyMessage={t('vehicles.table.empty')}
        isLoading={isLoading}
        pagination={tablePagination}
        rowKey={(vehicle): string => vehicle.id}
        rows={vehicles}
        sorting={tableSorting}
      />
      <VehicleDrawer
        carrierId={carrierId}
        onClose={(): void => setEditingVehicle(null)}
        open={editingVehicle !== null}
        scope={scope}
        siteId={siteId}
        spaceId={spaceId}
        vehicle={editingVehicle}
      />
      <ArchiveVehicleDialog onClose={(): void => setArchiveTarget('')} vehicleId={archiveTarget} />
    </>
  );
}
